import React from 'react';
import { Neo, NeoGrid, Transition, Views } from '@singularsystems/neo-react';
import InvestorVM from './InvestorVM';
import { observer } from 'mobx-react';
import InvestorPagedLookup from "../../Models/Investors/Lookups/InvestorPagedLookup";
import { Misc, ModalUtils, Validation } from "@singularsystems/neo-core";
import PortfolioTypeCatalogueEntry from "../Catalogue/GenericComponents/PortfolioTypeCatalogueEntry";
import { Catalogue } from '@singularsystems/neo-react-services';
import BankDetails from "../Components/BankDetails";
import TitleCatalogueEntry from '../Catalogue/GenericComponents/TitleCatalogueEntry';
import CountryCatalogueEntry from '../Catalogue/General/CountryCatalogueEntry';
import NotesGrid from '../Components/NotesGrid';
import AuthorisationComponents from '../../Components/AuthorisationComponents';
import AuthoriseButton from '../../Components/AuthoriseButton';
import * as Roles from '../../Models/Security/Roles';
import ContactAutoCompleteDropdown from '../Contacts/Components/ContactAutoCompleteDropdown';
import Investor from '../../Models/Investors/Investor';
import InvestorInvestmentView from '../../../Domain/Views/InvestorInvestments/InvestorInvestmentView';
import ContactTypeCatalogueEntry from '../Catalogue/General/ContactTypeCatalogueEntry';
import { isHidden } from '../../Models/Config/FieldConfig/FieldSetting.enum';
import SARSNatureOfPersonCatalogueEntry from '../Catalogue/General/SARSNatureOfPersonCatalogueEntry';
import IntraGroupCatalogueEntry from '../Catalogue/GenericComponents/IntraGroupCatalogueEntry';
import SectorCatalogueEntry from '../Catalogue/Reporting/SectorsCatalogueEntry';

class InvestorViewParams {
    public investorId = {};
}

@observer
export default class InvestorView extends Views.ViewBase<InvestorVM, InvestorViewParams> {
    public static params = new InvestorViewParams();

    constructor(props: unknown) {
        super("Investor", InvestorVM, props);
    }

    protected viewParamsUpdated(): void {
        this.viewModel.onInvestorParamChanged(this.viewParams.investorId);
    }

    public render() {
        var viewModel = this.viewModel;

        return (
            <div className="pt-3 mb-3">
                <Neo.TransitionContainer transition={Transition.upDown} backTransition={Transition.inOut}>
                    <Neo.TransitionPanel isVisible={!viewModel.editingInvestor}>
                        <Neo.Card title="Find Investor" className="mt-0">
                            <Neo.Form onSubmit={() => viewModel.autoRunner?.fireImmediate()} showSummaryModal>
                                <div style={{ textAlign: "right" }} className="mb-2">
                                    <Neo.Button onClick={() => this.viewParams.investorId.value = "new"}>Add Investor</Neo.Button>
                                </div>
                                <Neo.GridLayout lg={2}>
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.investorName} />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.accountNo} />
                                    <Neo.FormGroupInline label={"ID No./Company Reg No"} bind={viewModel.criteria.meta.idRegNo} />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showUnauthorised} />
                                    <Neo.FormGroupInline label="Investor Id" bind={viewModel.criteria.meta.investorId} />
                                </Neo.GridLayout>

                                <Neo.Pager pageManager={viewModel.pageManager} pageControls="bottom" >
                                    <NeoGrid.Grid<InvestorPagedLookup>>
                                        {(item, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column display={meta.investorId} width="15%" label="Investor Id" />
                                                <NeoGrid.Column display={meta.investorName} width="35%" />
                                                <NeoGrid.Column display={meta.legalEntityTypeName} width="35%" />
                                                <NeoGrid.Column display={meta.isAuthorised} width="15%" />
                                                <NeoGrid.ButtonColumn
                                                    editButton={{ onClick: () => this.viewParams.investorId.value = item.investorId }} />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Pager>
                            </Neo.Form>
                        </Neo.Card>
                    </Neo.TransitionPanel>
                    <Neo.TransitionPanel isVisible={viewModel.editingInvestor} animateHeight={false}>
                        {(investor: Investor) => this.renderInvestor(investor)}
                    </Neo.TransitionPanel>
                </Neo.TransitionContainer>

                <AuthorisationComponents viewModel={this.viewModel.authorisationVM} />
            </div>
        );
    }

    private renderInvestor(investor: Investor) {
        const viewModel = this.viewModel,
            meta = investor.meta,
            fieldSettings = viewModel.dataCache.fieldConfig.investorFields

        return (
            <Neo.Form model={investor} showSummaryModal onSubmit={() => viewModel.saveInvestor()} validationDisplayMode={Validation.DisplayMode.Always}>
                <Neo.Card title={"Investor - " + investor.toString()} className="mb-4 mt-0">
                    <div className="d-flex justify-content-between">
                        <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewParams.investorId.reset()}>Back</Neo.Button>
                        <div>
                            <Neo.Button icon="coins" className="me-2" variant="light" hidden={!investor.investorId} onClick={() => this.confirmSaveAndRedirect(investor)}>View Investments</Neo.Button>
                            <Neo.Button
                                className="min-width me-2"
                                tooltip={investor.isPendingDelete ? "Stop this investor from being removed when authorised." : "Mark this investor to be removed when authorised."}
                                icon={investor.isPendingDelete ? "trash-restore" : "trash"}
                                variant="danger"
                                hidden={!investor.investorId}
                                onClick={() => this.viewModel.setInvestorPendingDelete(investor)} />
                            <AuthoriseButton
                                className="me-2"
                                viewModel={viewModel.authorisationVM}
                                currentRecord={investor}
                                preAuthoriseRoleName={Roles.Investor.PreAuthorise}
                                authoriseRoleName={Roles.Investor.Authorise}
                                onDelete={() => {
                                    this.viewModel.pageManager.refreshData(true);
                                    this.viewParams.investorId.reset();
                                }} />
                            <Neo.Button isSubmit icon="save" pulse={investor.isDirty} disabled={!investor.isDirty}>Save</Neo.Button>
                        </div>
                    </div>
                </Neo.Card>
                <Neo.TabContainer>
                    <Neo.Tab header="Investor Details">
                        <Neo.GridLayout lg={2}>
                            <div>
                                <Neo.Card title="Investor Details">
                                    <Neo.FormGroupInline
                                        bind={meta.investorId}
                                        label="Ref No."
                                        readOnly={true} />
                                    <Neo.FormGroupInline
                                        bind={meta.investorReferralTypeId}
                                        hidden={isHidden(fieldSettings.investorReferralTypeId)}
                                        select={{
                                            itemSource: viewModel.dataCache.investorReferralTypes,
                                            onItemSelected: (investorReferralType) => viewModel.investorReferralTypeSelected(investorReferralType)
                                        }} />
                                    <Neo.FormGroupInline
                                        bind={meta.legalEntityTypeId}
                                        select={{
                                            itemSource: viewModel.dataCache.legalEntityTypes,
                                            onItemSelected: (legalEntityType) => viewModel.assignLegalEntity(legalEntityType)
                                        }} />
                                    <Neo.FormGroupInline
                                        bind={meta.idRegNo}
                                        label={investor.isIndividual ? "Legal ID No." : investor.isTrust ? "Masters Ref No." : "Company Reg No."}
                                        onBlur={() => investor.formatCompanyRegNo()} />
                                    <Neo.FormGroupInline
                                        bind={meta.passportNo}
                                        hidden={meta.legalEntityTypeId.value === null || !investor.isIndividual} />
                                    <Neo.FormGroupInline 
                                        bind={meta.internalAccountNo} 
                                        hidden={isHidden(fieldSettings.internalAccountNo)} 
                                        label={fieldSettings.internalAccountNoLabel ?? "Internal Account No"}  />
                                    <Neo.FormGroupInline 
                                        bind={meta.externalAccountNo} 
                                        hidden={isHidden(fieldSettings.externalAccountNo)} 
                                        label={fieldSettings.externalAccountNoLabel ?? "External Account No"} />
                                    <Neo.FormGroupInline 
                                        bind={meta.externalRelationshipManager} 
                                        hidden={isHidden(fieldSettings.externalAccountNo)}
                                        label={fieldSettings.externalRelationshipManagerLabel ?? "External Relationship Manager"} />
                                    <Neo.FormGroupInline
                                        bind={meta.investorName}
                                        hidden={meta.legalEntityTypeId.value === null || investor.isIndividual} />
                                    <Catalogue.FormGroupInline
                                        bind={meta.titleId}
                                        catalogueType={TitleCatalogueEntry}
                                        hidden={meta.legalEntityTypeId.value === null || !investor.isIndividual} />
                                    <Neo.FormGroupInline
                                        bind={meta.firstName}
                                        hidden={meta.legalEntityTypeId.value === null || !investor.isIndividual} />
                                    <Neo.FormGroupInline
                                        bind={meta.lastName}
                                        hidden={meta.legalEntityTypeId.value === null || !investor.isIndividual} />
                                    <Neo.FormGroupInline bind={meta.shortName} hidden={isHidden(fieldSettings.shortName)} />
                                    <Neo.FormGroupInline bind={meta.taxRefNo} hidden={isHidden(fieldSettings.taxRefNo)} />
                                    <Neo.FormGroupInline bind={meta.postalAddress} input={{ rows: 3 }} hidden={isHidden(fieldSettings.postalAddress)} />
                                    <Neo.FormGroupInline bind={meta.physicalAddress} input={{ rows: 3 }} hidden={isHidden(fieldSettings.physicalAddress)} />
                                    <Catalogue.FormGroupInline bind={meta.countryId} catalogueType={CountryCatalogueEntry} hidden={isHidden(fieldSettings.countryId)} />
                                    <Catalogue.FormGroupInline bind={meta.sarsNatureOfPersonId} catalogueType={SARSNatureOfPersonCatalogueEntry} hidden={isHidden(fieldSettings.sarsNatureOfPersonId)} />
                                    <Catalogue.FormGroupInline bind={meta.intraGroupId} catalogueType={IntraGroupCatalogueEntry} hidden={isHidden(fieldSettings.intraGroupId)} />
                                    <Catalogue.FormGroupInline bind={meta.sectorId} catalogueType={SectorCatalogueEntry} hidden={isHidden(fieldSettings.sectorId)} />
                                </Neo.Card>
                                <Neo.Card title="Bank Details">
                                    <div>
                                        <BankDetails bankDetail={investor.bankDetails} />
                                    </div>
                                </Neo.Card>
                                <Neo.Card title="Secondary Bank Details" hidden={isHidden(fieldSettings.additionalBankDetails)}>
                                    <div>
                                        <Neo.Button className="mb-3" variant="secondary" size="sm" onClick={() => viewModel.toggleBankDetails()} >
                                            {investor.additionalBankDetails.length === 0 ? 'Add Secondary Bank Details' : 'Remove Secondary Bank Details'}
                                        </Neo.Button>

                                        {investor.additionalBankDetails.length > 0 &&
                                            <div>
                                                <BankDetails bankDetail={investor.additionalBankDetails[0].values} />
                                            </div>
                                        }
                                    </div>
                                </Neo.Card>
                                <Neo.Card title="Contacts">
                                    <Neo.FormGroupInline>
                                        <ContactAutoCompleteDropdown
                                            bind={investor.meta.authorisedPersonContactId}
                                            bindDisplay={investor.meta.authorisedContactName}
                                            parentName="investor"
                                        />
                                    </Neo.FormGroupInline>
                                    <Neo.FormGroupInline>
                                        <ContactAutoCompleteDropdown
                                            bind={investor.meta.referralContactId}
                                            bindDisplay={investor.meta.referralContactName}
                                            parentName="investor"
                                        />
                                    </Neo.FormGroupInline>
                                    <Neo.FormGroupInline
                                        bind={meta.contactEmailsDisplay}
                                        label="Additional email contacts"
                                        input={{ rows: 3 }}
                                        footerText="Enter each email address on a new line" />
                                    <NeoGrid.Grid items={investor.contacts} showAddButton>
                                        {(item, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column bindContext={item.meta.contactId}>
                                                    <ContactAutoCompleteDropdown
                                                        bind={item.meta.contactId}
                                                        bindDisplay={item.meta.contactName}
                                                        parentName="investor"
                                                    />
                                                </NeoGrid.Column>
                                                <NeoGrid.Column >
                                                </NeoGrid.Column>
                                                <Catalogue.Column bind={item.meta.contactTypeId} catalogueType={ContactTypeCatalogueEntry} />
                                                <NeoGrid.ButtonColumn showDelete />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Card>
                            </div>
                            <div>
                                <Neo.Card title="Beneficiary Details">
                                    <Neo.FormGroupInline bind={meta.withholdingAgentRefNumber} hidden={isHidden(fieldSettings.withholdingAgentRefNumber)} />
                                    <Neo.FormGroupInline bind={meta.beneficiaryName} hidden={isHidden(fieldSettings.beneficiaryName)} />
                                    <Neo.FormGroupInline bind={meta.beneficiaryTaxReferenceNumber} hidden={isHidden(fieldSettings.beneficiaryTaxReferenceNumber)} />
                                    <Neo.FormGroupInline bind={meta.beneficiaryPhysicalAddress} input={{ rows: 3 }} hidden={isHidden(fieldSettings.beneficiaryPhysicalAddress)} />
                                    <Neo.FormGroupInline bind={meta.dwtExpiryDate} label="DWT Expiry Date" hidden={isHidden(fieldSettings.dwtExemptionId)} />
                                    <Neo.FormGroupInline label={"DWT Exemption Code"} hidden={isHidden(fieldSettings.dwtExemptionId)} >
                                        <Neo.AutoCompleteDropDown
                                            items={viewModel.dataCache.dwtExemptions}
                                            bind={meta.dwtExemptionId}
                                            displayMember="code"
                                            descriptionMember="description"
                                        />
                                    </Neo.FormGroupInline>
                                    <Neo.FormGroupInline bind={meta.dwtReducedRate} appendText="%" label="DWT Reduced Rate" hidden={isHidden(fieldSettings.dwtExemptionId)} />
                                    <Catalogue.FormGroupInline bind={meta.portfolioTypeId} catalogueType={PortfolioTypeCatalogueEntry} hidden={isHidden(fieldSettings.portfolioTypeId)} />
                                </Neo.Card>
                                <Neo.Card title="Other Details">
                                    <Neo.FormGroupInline bind={meta.ficaDate} hidden={isHidden(fieldSettings.ficaDate)} />
                                    <Neo.FormGroupInline bind={meta.sanctionScreenPerformed} label='Sanctioned screening performed?' hidden={isHidden(fieldSettings.sanctionedScreenPerformed)} />
                                    <Neo.FormGroupInline bind={meta.commission} hidden={isHidden(fieldSettings.commission)} />
                                    <Neo.FormGroupInline bind={meta.commissionDiv} hidden={isHidden(fieldSettings.commissionDiv)} />
                                    <Neo.FormGroupInline bind={meta.ledgerAccount} hidden={isHidden(fieldSettings.ledgerAccount)} />
                                    <Neo.FormGroupInline bind={meta.isRestricted} label='Restricted from trading?' hidden={isHidden(fieldSettings.isRestricted)} />
                                    <Neo.FormGroupInline bind={meta.isLetterPasswordProtected} label='Password protect dividend letters?' hidden={isHidden(fieldSettings.isLetterPasswordProtected)} />
                                </Neo.Card>
                                <Neo.Card title="Notes">
                                    <NotesGrid parentModel={investor} viewModel={viewModel.noteVM} />
                                </Neo.Card>
                            </div>
                        </Neo.GridLayout>
                    </Neo.Tab>
                    <Neo.Tab header="Users">
                        <Neo.GridLayout>
                            <NeoGrid.Grid items={investor.users} addButton={{ text: "Link User" }}>
                                {(item, meta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column label="User" bind={meta.portalUserId} readOnly={!item.isNew} select={{ itemSource: viewModel.portalUserLookupDataSource, renderAsText: !item.isNew }} />
                                        <NeoGrid.ButtonColumn showDelete />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                            <div>
                                <Neo.FormGroup bind={meta.requiredClientAuthorisations} label="Additional Required Client Authorisations"
                                    footerText="No. of client users who need to approve requests in addition to the initiator." />
                            </div>
                        </Neo.GridLayout>
                    </Neo.Tab>
                </Neo.TabContainer>
            </Neo.Form>)
    }

    private async confirmSaveAndRedirect(investor: Investor) {
        if (investor.readyToSave && await ModalUtils.showYesNoDismissible('save', `Would you like to save the changes that were made to this investor?`) === Misc.ModalResult.Yes) {
            await this.viewModel.saveInvestor();
        }
        InvestorVM.lastInvestor = investor;
        this.navigation.navigateToView(InvestorInvestmentView, { investorId: investor.investorId })
    }

    public onLeave(): string | undefined {
        if (this.viewModel.editingInvestor?.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}